import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  type User,
} from 'firebase/auth';
import type { Claims, Role } from '@helios/authz';
import { getAuthInstance } from './firebase-init';
import { mapAuthError, mapPasswordResetError } from './errors';

export async function signInWithEmailPassword(email: string, password: string): Promise<User> {
  try {
    const auth = getAuthInstance();
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error: unknown) {
    const code =
      error && typeof error === 'object' && 'code' in error ? (error.code as string) : 'unknown';
    throw new Error(mapAuthError(code));
  }
}

export async function signOut(): Promise<void> {
  const auth = getAuthInstance();
  await firebaseSignOut(auth);
}

export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const auth = getAuthInstance();
  const user = auth.currentUser;

  if (!user) return null;

  return user.getIdToken(forceRefresh);
}

/**
 * Send a Firebase password reset email to the currently authenticated user.
 *
 * PRD.303: takes NO target email by design. The address is read from the
 * signed-in Firebase user, so no caller can request a reset for another
 * account. Uses Firebase's default action handler — no ActionCodeSettings /
 * continue URL, so no Authorized Domains configuration is involved.
 *
 * Always throws an `Error` whose message is already user-safe (mapped through
 * `mapPasswordResetError`); the raw Firebase error never escapes.
 */
export async function sendPasswordResetForCurrentUser(): Promise<void> {
  const auth = getAuthInstance();
  const email = auth.currentUser?.email;

  // Fail before touching Firebase when there is no usable account email.
  if (!email) {
    throw new Error(mapPasswordResetError('auth/missing-email'));
  }

  try {
    await firebaseSendPasswordResetEmail(auth, email);
  } catch (error: unknown) {
    const code =
      error && typeof error === 'object' && 'code' in error ? (error.code as string) : 'unknown';
    throw new Error(mapPasswordResetError(code));
  }
}

/**
 * Request a Firebase password reset email for an arbitrary address, for the
 * UNAUTHENTICATED login recovery flow (PRD.304).
 *
 * Deliberately separate from `sendPasswordResetForCurrentUser()`, which serves
 * the authenticated Account Settings flow and takes no email. Do not merge them:
 * the split is what keeps Account Settings structurally current-user-only.
 *
 * Anti-enumeration: the login page is public, so `auth/user-not-found` resolves
 * as success rather than throwing. Callers therefore cannot distinguish a
 * registered address from an unregistered one. (Firebase's own Email
 * Enumeration Protection already behaves this way where enabled; this makes the
 * behavior identical regardless of that project setting.)
 *
 * Every other failure throws an `Error` whose message is already user-safe.
 */
export async function requestPasswordResetForEmail(email: string): Promise<void> {
  const auth = getAuthInstance();
  const trimmed = email.trim();

  if (!trimmed) {
    throw new Error(mapPasswordResetError('auth/missing-email'));
  }

  try {
    await firebaseSendPasswordResetEmail(auth, trimmed);
  } catch (error: unknown) {
    const code =
      error && typeof error === 'object' && 'code' in error ? (error.code as string) : 'unknown';
    // Account-existence signal — swallow so the public UI cannot enumerate.
    if (code === 'auth/user-not-found') return;
    throw new Error(mapPasswordResetError(code));
  }
}

export async function getClaims(user: User): Promise<Claims> {
  const idTokenResult = await user.getIdTokenResult();
  return {
    orgs: idTokenResult.claims.orgs as Record<string, { roles: Role[] }> | undefined,
    activeOrg: idTokenResult.claims.activeOrg as string | undefined,
    activeSuborg: idTokenResult.claims.activeSuborg as string | null | undefined,
  };
}
